import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
  makeStyles,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import GetAppIcon from "@material-ui/icons/GetApp";
import ButtonWithLoading from "components/ButtonWithLoading";
import { downloadFromLink } from "utils/downloadFile";
import type { MalwareContent, MalwareFile } from "@dashboard-v3/api";
import { fetchFileDownloadLink } from "../utils";

type Props = {
  file: MalwareFile;
  content: MalwareContent;
  onClose: () => void;
};

export default function DownloadWarningDialog({
  file,
  content,
  onClose,
}: Props) {
  const { t } = useTranslation("mxheroShares");
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [accepted, setAccepted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // A link that arrives after the dialog was closed must not start a download
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  async function download() {
    setDownloading(true);
    try {
      const { url } = await fetchFileDownloadLink(file.id, content.checksum);
      if (!mounted.current) return;
      // The file name comes from the Content-Disposition header of the link
      downloadFromLink(url);
      onClose();
    } catch (error) {
      if (!mounted.current) return;
      enqueueSnackbar(t("malware.errors.download"), { variant: "error" });
      setDownloading(false);
    }
  }

  // While the link is being requested the dialog cannot be dismissed
  function onDialogClose() {
    if (!downloading) onClose();
  }

  const acceptInputProps = {
    "data-testid": "malware-download-accept",
  } as React.InputHTMLAttributes<HTMLInputElement>;

  return (
    <Dialog open onClose={onDialogClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("malware.download.title")}</DialogTitle>
      <DialogContent>
        <div className={classes.warning}>
          <WarningIcon fontSize="small" />
          <Typography variant="body2">
            {t("malware.download.warning", {
              file: file.name,
              threat: content.threat,
            })}
          </Typography>
        </div>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={accepted}
              onChange={event => setAccepted(event.target.checked)}
              inputProps={acceptInputProps}
            />
          }
          label={
            <Typography variant="body2">
              {t("malware.download.accept")}
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={downloading}>
          {t("common:cancel")}
        </Button>
        <ButtonWithLoading
          variant="contained"
          disableElevation
          className={classes.danger}
          startIcon={<GetAppIcon />}
          disabled={!accepted}
          loading={downloading}
          data-testid="malware-download-confirm"
          onClick={download}
        >
          {t("malware.actions.download")}
        </ButtonWithLoading>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  warning: {
    display: "flex",
    gap: 12,
    padding: "12px 14px",
    marginBottom: 16,
    borderRadius: 4,
    backgroundColor: "#fff4e5",
    color: "#663c00",
    "& svg": { color: theme.palette.warning.main, flexShrink: 0 },
  },
  danger: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": { backgroundColor: theme.palette.error.dark },
  },
}));
